import { Sun, ScrollText, Landmark, Quote } from "lucide-react";
import { Reveal } from "./Reveal";
import { openSabbathQuotes } from "./SabbathQuotes";

const eras = [
  {
    icon: Sun,
    era: "The First Century",
    heading: "Sabbath Observance",
    intro:
      "The earliest Christians were Jewish. They continued their existing Jewish habits while adding new Christian ones.",
    points: [
      { label: "Sabbath Keeping", text: "They rested and worshiped in synagogues on Saturday." },
      { label: "The First Day", text: "They also met privately on Sunday evening." },
      {
        label: "The Reason",
        text: "These Sunday gatherings focused on breaking bread and sharing meals — not to observe a new Sabbath Day, as some suggest.",
      },
    ],
  },
  {
    icon: ScrollText,
    era: "The Second & Third Centuries",
    heading: "The Shift",
    intro:
      "As more non-Jewish people became Christians, the connection to Jewish customs weakened. Sunday gradually became the main day of worship.",
    quotes: [
      { who: "Ignatius of Antioch (c. 110 AD)", text: "Christians were \u201cno longer observing the Sabbath, but living in the observance of the Lord's Day.\u201d" },
      { who: "Justin Martyr (c. 150 AD)", text: "Described Christians meeting on \u201cthe day called Sunday,\u201d reading the writings of the apostles and prophets together." },
      { who: "Tertullian (c. 200 AD)", text: "Christians made Sunday a day of solemnity, putting off their daily business to avoid giving place to the devil." },
    ],
    footnote:
      "Even so, in many Eastern churches believers still honored both Saturday and Sunday as holy days.",
  },
  {
    icon: Landmark,
    era: "The Fourth Century",
    heading: "Legal & Official Changes",
    intro:
      "In the 300s, Sunday worship became both a legal requirement and an official church rule.",
    quotes: [
      { who: "Constantine's Edict (321 AD)", text: "The Roman Emperor made Sunday a legal day of rest: \u201con the venerable Day of the Sun let the magistrates and people residing in cities rest.\u201d" },
      { who: "Council of Laodicea (c. 363 AD)", text: "The Church officially forbade \u201cjudaizing\u201d on Saturday, ruling that Christians \u201cmust not judaize by resting on the Sabbath, but must work on that day, rather honoring the Lord's Day.\u201d" },
    ],
  },
];

export const Sabbath = () => (
  <section id="sabbath" data-testid="sabbath-section" className="bg-[#0A0A0A] py-24 md:py-32">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
          The Seventh-Day Sabbath
        </p>
        <h2 className="font-heading font-light tracking-tight text-4xl md:text-6xl leading-tight mb-6">
          From Sabbath to <span className="italic text-[#D4AF37]">Sunday</span>
        </h2>
        <p className="text-base md:text-lg leading-relaxed font-light text-[#A39E98] mb-16">
          Early Christians transitioned from honoring the seventh-day Sabbath
          (Saturday) to observing the Lord's Day (Sunday) over several hundred
          years. This is how it happened.
        </p>
        <button
          onClick={openSabbathQuotes}
          data-testid="sabbath-quotes-trigger"
          className="group inline-flex items-center gap-3 border border-[#4A453F] bg-[#141311] px-6 py-4 mb-16 hover:border-[#D4AF37] transition-colors"
        >
          <Quote strokeWidth={1.5} className="h-5 w-5 text-[#D4AF37]" />
          <span className="text-[#F4F0EA] font-medium group-hover:text-[#D4AF37] transition-colors">
            Famous Quotes About the Sabbath Change
          </span>
        </button>
      </Reveal>

      <div className="relative">
        {/* vertical line */}
        <div className="absolute left-[19px] top-2 bottom-2 w-px bg-[#2B2825]" aria-hidden="true" />

        <div className="space-y-14">
          {eras.map((e, i) => (
            <Reveal key={e.era} delay={i * 0.08}>
              <article data-testid={`sabbath-era-${i}`} className="relative pl-16">
                <span className="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37] bg-[#0A0A0A] text-[#D4AF37]">
                  <e.icon strokeWidth={1.5} className="h-5 w-5" />
                </span>

                <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                  {e.era}
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-light text-[#F4F0EA] mb-4">
                  {e.heading}
                </h3>
                <p className="text-base leading-relaxed font-light text-[#A39E98] mb-6">
                  {e.intro}
                </p>

                {e.points && (
                  <ul className="space-y-3">
                    {e.points.map((p) => (
                      <li key={p.label} className="border-l-2 border-[#2B2825] pl-4">
                        <span className="text-[#F4F0EA] font-medium">{p.label}: </span>
                        <span className="text-[#A39E98] font-light">{p.text}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {e.quotes && (
                  <div className="space-y-5">
                    {e.quotes.map((q) => (
                      <blockquote key={q.who} className="border-l-2 border-[#D4AF37] pl-5">
                        <p className="text-base leading-relaxed font-light italic text-[#F4F0EA]/90">
                          {q.text}
                        </p>
                        <cite className="mt-2 block font-mono text-xs not-italic tracking-wide text-[#D4AF37]">
                          — {q.who}
                        </cite>
                      </blockquote>
                    ))}
                  </div>
                )}

                {e.footnote && (
                  <p className="mt-6 text-sm leading-relaxed font-light text-[#A39E98]/80 italic">
                    {e.footnote}
                  </p>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.1}>
        <p className="mt-16 font-heading italic text-xl md:text-2xl font-light text-[#D4AF37] text-center">
          "Remember the Sabbath day, to keep it holy."
        </p>
      </Reveal>
    </div>
  </section>
);
